/** @jsxImportSource @emotion/react */
import { Link } from "react-router-dom";

// Un mensaje de la conversación con el asistente. Las respuestas traen links
// de navegación [texto](link:/ruta), **negritas** y tablas en markdown.

const REGEX_LINK = /\[([^\]]+)\]\(link:([^)]+)\)/;

function renderNegritas(texto) {
  const partes = texto.split(/(\*\*[^*]+\*\*)/g);
  return partes.map((parte, i) => {
    const match = parte.match(/^\*\*(.+)\*\*$/);
    if (match) {
      return <strong key={i}>{match[1]}</strong>;
    }
    return parte;
  });
}

// Los links van como texto (no como botón) para no cortar la lectura. Navegan
// en la misma pestaña; ctrl/cmd + click abre otra (lo resuelve Link).
function renderTextoConLinks(texto, onNavegar) {
  const partes = texto.split(/(\[[^\]]+\]\(link:[^)]+\))/g);
  return partes.map((parte, i) => {
    const matchLink = parte.match(REGEX_LINK);
    if (matchLink) {
      const [, textoLink, ruta] = matchLink;
      return (
        <Link
          key={i}
          to={ruta}
          onClick={(e) => {
            if (!e.metaKey && !e.ctrlKey) onNavegar?.();
          }}
          css={{
            color: "var(--principal)",
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" }
          }}
        >
          {renderNegritas(textoLink)}
        </Link>
      );
    }
    return <span key={i}>{renderNegritas(parte)}</span>;
  });
}

function esFilaDeTabla(linea) {
  return /^\s*\|.*\|\s*$/.test(linea);
}

function parsearFilaDeTabla(linea) {
  return linea
    .trim()
    .slice(1, -1)
    .split("|")
    .map((celda) => celda.trim());
}

function esSeparadorDeTabla(celdas) {
  return celdas.length > 0 && celdas.every((c) => /^:?-{2,}:?$/.test(c));
}

function renderTabla(lineas, key, onNavegar) {
  const filas = lineas.map(parsearFilaDeTabla);
  const tieneHeader = filas.length > 1 && esSeparadorDeTabla(filas[1]);
  const header = tieneHeader ? filas[0] : null;
  const cuerpo = (tieneHeader ? filas.slice(2) : filas).filter(
    (f) => !esSeparadorDeTabla(f)
  );
  return (
    <div key={key} css={{ overflowX: "auto", margin: "6px 0" }}>
      <table
        css={{
          borderCollapse: "collapse",
          "& th, & td": {
            border: "1px solid rgba(0, 0, 0, 0.2)",
            padding: "4px 10px",
            textAlign: "left"
          },
          "& th": {
            backgroundColor: "rgba(0, 0, 0, 0.05)"
          }
        }}
      >
        {header && (
          <thead>
            <tr>
              {header.map((celda, i) => (
                <th key={i}>{renderTextoConLinks(celda, onNavegar)}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {cuerpo.map((fila, i) => (
            <tr key={i}>
              {fila.map((celda, j) => (
                <td key={j}>{renderTextoConLinks(celda, onNavegar)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Las líneas consecutivas que son filas de tabla se agrupan y se dibujan como
// tabla; el resto, línea por línea.
function renderContenido(texto, onNavegar) {
  const bloques = [];
  let tabla = [];
  texto.split("\n").forEach((linea) => {
    if (esFilaDeTabla(linea)) {
      tabla.push(linea);
    } else {
      if (tabla.length > 0) {
        bloques.push({ tipo: "tabla", lineas: tabla });
        tabla = [];
      }
      bloques.push({ tipo: "linea", linea });
    }
  });
  if (tabla.length > 0) bloques.push({ tipo: "tabla", lineas: tabla });

  return bloques.map((bloque, i) =>
    bloque.tipo === "tabla" ? (
      renderTabla(bloque.lineas, i, onNavegar)
    ) : (
      <span key={i}>
        {renderTextoConLinks(bloque.linea, onNavegar)}
        <br />
      </span>
    )
  );
}

export default function MensajeAsistente({ rol, texto, error, onNavegar }) {
  if (rol === "user") {
    return (
      <div
        css={{
          alignSelf: "flex-end",
          maxWidth: "85%",
          backgroundColor: "var(--principal)",
          color: "white",
          borderRadius: "10px 10px 0px 10px",
          padding: "8px 12px",
          whiteSpace: "pre-wrap"
        }}
      >
        {texto}
      </div>
    );
  }
  return (
    <div
      css={{
        alignSelf: "flex-start",
        maxWidth: "95%",
        backgroundColor: "#f2f2f2",
        borderRadius: "10px 10px 10px 0px",
        padding: "8px 12px",
        // Respuesta que no se pudo generar
        ...(error && { color: "var(--rojo)", fontStyle: "italic" })
      }}
    >
      {renderContenido(texto, onNavegar)}
    </div>
  );
}
